import type {
  Attachment,
  Scenario,
  ScenarioContent,
  CreateScenarioInput,
  Paginated,
  Questionnaire,
  MCQConfig,
  RolePlayArtifact,
  RolePlayConfig,
  UpdateScenarioInput,
  UpdateScenarioMetaInput,
  ScoringJobResponse,
  UserGroup,
  Share,
} from "./types";
import { TOKEN_KEY } from "@/lib/auth/PowtoonAuthContext";
import { getOrgDomain } from "@/lib/powtoon-oauth";
import { createLogger } from "@/lib/frontend-logger";
import { CORRELATION_HEADER, nextCorrelationId } from "@/lib/correlation";
import { appendOp } from "@/lib/opSlug";

const logger = createLogger("powtoon-be.client");

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const INTEGRATION_BASE = `${SUPABASE_URL}/functions/v1/powtoon-integration`;
const BASE = "/api/v1.0/integrations/ai-app-projects";
const SCORING_BASE = "/api/v1.0/live-avatar/scoring-jobs";

function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem(TOKEN_KEY);
}

function authHeaders(opts: { skipUserAuth?: boolean } = {}): Record<string, string> {
  const h: Record<string, string> = {
    apikey: SUPABASE_KEY,
    authorization: `Bearer ${SUPABASE_KEY}`,
  };
  const org = getOrgDomain();
  if (org) h["x-powtoon-org"] = org;
  if (!opts.skipUserAuth) {
    const tok = getToken();
    if (tok) h["x-powtoon-token"] = tok;
  }
  return h;
}

async function call<T>(
  path: string,
  init?: RequestInit & { skipUserAuth?: boolean; op?: string },
): Promise<T> {
  const { skipUserAuth, op, ...rest } = init ?? {};
  const correlationId = nextCorrelationId();
  const startedAt = performance.now();
  const method = rest.method ?? "GET";
  const res = await fetch(appendOp(`${INTEGRATION_BASE}${path}`, op), {
    ...rest,
    headers: {
      "content-type": "application/json",
      ...authHeaders({ skipUserAuth }),
      [CORRELATION_HEADER]: correlationId,
      ...(rest.headers ?? {}),
    },
  });
  const durationMs = Math.round(performance.now() - startedAt);
  if (!res.ok) {
    const reqId = res.headers.get("x-req-id");
    const body = await res.text().catch(() => "");
    const snippet = body.length > 300 ? body.slice(0, 300) + "…" : body;
    const msg = `powtoon-be ${res.status} on ${path}${reqId ? ` [${reqId}]` : ""}${snippet ? `: ${snippet}` : ""}`;
    logger.error("powtoon-be call failed", {
      method, path, status: res.status, reqId, correlationId, durationMs, snippet,
    });
    throw new Error(msg);
  }
  logger.info("powtoon-be call ok", {
    method, path, status: res.status, correlationId, durationMs,
  });
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

const req = call;

async function reqList<T>(
  path: string,
  init?: RequestInit & { op?: string },
): Promise<T[]> {
  const data = await call<Paginated<T> | T[]>(path, init);
  if (Array.isArray(data)) return data;
  return data.results ?? [];
}

function qs(params: Record<string, string | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v != null) u.set(k, v);
  const s = u.toString();
  return s ? `?${s}` : "";
}

export const powtoonBE = {
  scenarios: {
    list: () =>
      reqList<Scenario>(`${BASE}${qs({ include_content: "true" })}`, { op: "list-scenarios" }),

    get: async (id: string, opts: { publicLinkToken?: string } = {}) => {
      const data = await req<Scenario | Scenario[]>(
        `${BASE}/${id}${qs({ include_content: "true", public_link_token: opts.publicLinkToken })}`,
        { op: "get-scenario", ...(opts.publicLinkToken ? { skipUserAuth: true } : {}) },
      );
      if (Array.isArray(data)) {
        if (!data[0]) throw new Error("Scenario not found");
        return data[0];
      }
      return data;
    },

    createPublicLinkToken: (id: string) =>
      req<{ public_link_token: string }>(`${BASE}/${id}/public-link-token`, {
        method: "POST",
        op: "create-public-link-token",
      }),

    create: async (input: CreateScenarioInput): Promise<Scenario> => {
      const { content, ...meta } = input;
      const created = await req<Scenario>(BASE, {
        method: "POST",
        body: JSON.stringify({ application: "live-avatars", ...meta }),
        op: "create-scenario",
      });
      if (content && Object.keys(content).length > 0) {
        return req<Scenario>(`${BASE}/${created.id}`, {
          method: "PUT",
          body: JSON.stringify({ content }),
          op: "update-scenario-content",
        });
      }
      return created;
    },

    setThumbnailFromUrl: async (
      scenarioId: string,
      sourceUrl: string,
      backgroundUrl?: string,
    ) => {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/powtoon-thumb`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          scenario_id: scenarioId,
          source_url: sourceUrl,
          background_url: backgroundUrl,
        }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`powtoon-thumb ${res.status}: ${body.slice(0, 300)}`);
      }
      return res.json() as Promise<Scenario>;
    },

    updateMeta: (id: string, input: UpdateScenarioMetaInput) =>
      req<Scenario>(`${BASE}/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
        op: "update-scenario-meta",
      }),

    updateContent: (id: string, content: ScenarioContent) =>
      req<Scenario>(`${BASE}/${id}`, {
        method: "PUT",
        body: JSON.stringify({ content }),
        op: "update-scenario-content",
      }),

    remove: (id: string) =>
      req<void>(`${BASE}/${id}`, { method: "DELETE", op: "delete-scenario" }),

    listShares: (id: string) =>
      reqList<Share>(`${BASE}/${id}/shares`, { op: "list-shares" }),

    shareWithGroup: (id: string, groupId: string) =>
      req<Share>(`${BASE}/${id}/shares`, {
        method: "POST",
        body: JSON.stringify({
          grantee_type: "group",
          grantee_id: groupId,
          permissions: ["can_view"],
        }),
        op: "share-with-group",
      }),

    shareWithEmail: (id: string, email: string, canEdit = false) =>
      req<Share>(`${BASE}/${id}/shares/create-by-email`, {
        method: "POST",
        body: JSON.stringify({
          email,
          permissions: canEdit ? ["can_view", "can_edit"] : ["can_view"],
        }),
        op: "share-with-email",
      }),

    removeShare: (id: string, shareId: string) =>
      req<void>(`${BASE}/${id}/shares/${shareId}`, {
        method: "DELETE",
        op: "delete-share",
      }),

    update: async (id: string, input: UpdateScenarioInput) => {
      const { content, ...meta } = input;
      let result: Scenario | undefined;
      if (Object.keys(meta).length > 0) {
        result = await req<Scenario>(`${BASE}/${id}`, {
          method: "PATCH",
          body: JSON.stringify(meta),
          op: "update-scenario-meta",
        });
      }
      if (content) {
        result = await req<Scenario>(`${BASE}/${id}`, {
          method: "PUT",
          body: JSON.stringify({ content }),
          op: "update-scenario-content",
        });
      }
      return result as Scenario;
    },
  },

  sessions: {
    track: (
      scenarioId: string,
      content: Record<string, unknown>,
      opts: { publicLinkToken?: string } = {},
    ) =>
      req<{ id: string }>(
        `${BASE}/${scenarioId}/objects${qs({ public_link_token: opts.publicLinkToken })}`,
        {
          method: "POST",
          body: JSON.stringify({
            object_type: "live_avatar_session",
            name: "Live Avatar Session",
            content: opts.publicLinkToken
              ? { ...content, public_link_token: opts.publicLinkToken }
              : content,
          }),
          op: "track-session",
          ...(opts.publicLinkToken ? { skipUserAuth: true } : {}),
        },
      ),

    trackTranscript: (
      scenarioId: string,
      content: Record<string, unknown>,
      opts: { publicLinkToken?: string } = {},
    ) =>
      req<{ id: string }>(
        `${BASE}/${scenarioId}/objects${qs({ public_link_token: opts.publicLinkToken })}`,
        {
          method: "POST",
          body: JSON.stringify({
            object_type: "live_avatar_session_transcript",
            name: "Live Avatar Session Transcript",
            content: opts.publicLinkToken
              ? { ...content, public_link_token: opts.publicLinkToken }
              : content,
          }),
          op: "track-transcript",
          ...(opts.publicLinkToken ? { skipUserAuth: true } : {}),
        },
      ),

    /** Read one stored `live_avatar_session_transcript` artifact (analytics export). */
    getTranscript: async (scenarioId: string, objectId: string) => {
      const data = await req<
        | { id: string; content?: Record<string, unknown> }
        | { id: string; content?: Record<string, unknown> }[]
      >(`${BASE}/${scenarioId}/objects/${objectId}${qs({ include_content: "true" })}`, {
        op: "get-transcript",
      });
      if (Array.isArray(data)) {
        if (!data[0]) throw new Error("Transcript not found");
        return data[0];
      }
      return data;
    },
  },


  attachments: {
    list: (scenarioId: string) =>
      reqList<Attachment>(
        `${BASE}/${scenarioId}/objects${qs({ object_type: "attachment", include_content: "true" })}`,
        { op: "list-attachments" },
      ),

    get: async (
      scenarioId: string,
      objectId: string,
      opts: { publicLinkToken?: string } = {},
    ) => {
      const data = await req<Attachment | Attachment[]>(
        `${BASE}/${scenarioId}/objects/${objectId}${qs({ include_content: "true", public_link_token: opts.publicLinkToken })}`,
        { op: "get-attachment", ...(opts.publicLinkToken ? { skipUserAuth: true } : {}) },
      );
      if (Array.isArray(data)) {
        if (!data[0]) throw new Error("Attachment not found");
        return data[0];
      }
      return data;
    },

    upload: (scenarioId: string, file: File, name?: string) => {
      const fd = new FormData();
      fd.append("object_type", "attachment");
      fd.append("file", file);
      if (name) fd.append("name", name);
      const headers = authHeaders();
      return fetch(
        appendOp(`${INTEGRATION_BASE}${BASE}/${scenarioId}/objects`, "upload-attachment"),
        {
        method: "POST",
        headers,
        body: fd,
      }).then((r) => {
        if (!r.ok) throw new Error(`upload ${r.status}`);
        return r.json() as Promise<Attachment>;
      });
    },

    remove: (scenarioId: string, objectId: string) =>
      req<void>(`${BASE}/${scenarioId}/objects/${objectId}`, {
        method: "DELETE",
        op: "delete-attachment",
      }),
  },

  questionnaires: {
    get: async (scenarioId: string, objectId: string) => {
      const data = await req<Questionnaire | Questionnaire[]>(
        `${BASE}/${scenarioId}/objects/${objectId}${qs({ include_content: "true" })}`,
        { op: "get-questionnaire" },
      );
      if (Array.isArray(data)) {
        if (!data[0]) throw new Error("Questionnaire not found");
        return data[0];
      }
      return data;
    },

    list: (scenarioId: string) =>
      reqList<Questionnaire>(
        `${BASE}/${scenarioId}/objects${qs({ object_type: "live_avatar_questionnaire" })}`,
        { op: "list-questionnaires" },
      ),

    create: (scenarioId: string, mcq: MCQConfig, name = "Questionnaire") =>
      req<Questionnaire>(`${BASE}/${scenarioId}/objects`, {
        method: "POST",
        body: JSON.stringify({
          object_type: "live_avatar_questionnaire",
          name,
          content: mcq,
        }),
        op: "create-questionnaire",
      }),
  },

  /**
   * Versioned role-play artifacts. Same `/objects` endpoint as the
   * questionnaire, different `object_type`. The Powtoon BE scoring job reads
   * the newest one for the project when a role-play session is scored.
   */
  roleplays: {
    get: async (scenarioId: string, objectId: string) => {
      const data = await req<RolePlayArtifact | RolePlayArtifact[]>(
        `${BASE}/${scenarioId}/objects/${objectId}${qs({ include_content: "true" })}`,
        { op: "get-roleplay" },
      );
      if (Array.isArray(data)) {
        if (!data[0]) throw new Error("Role play not found");
        return data[0];
      }
      return data;
    },

    list: (scenarioId: string) =>
      reqList<RolePlayArtifact>(
        `${BASE}/${scenarioId}/objects${qs({ object_type: "live_avatar_roleplay" })}`,
        { op: "list-roleplays" },
      ),

    create: (scenarioId: string, roleplay: RolePlayConfig, name = "Role play") =>
      req<RolePlayArtifact>(`${BASE}/${scenarioId}/objects`, {
        method: "POST",
        body: JSON.stringify({
          object_type: "live_avatar_roleplay",
          name,
          content: roleplay,
        }),
        op: "create-roleplay",
      }),
  },

  scoringJobs: {
    // (role-play scoring uses the same endpoint; the BE resolves the
    // `live_avatar_roleplay` artifact from the transcript's project)
    start: (
      transcriptObjectId: string,
      opts: {
        scenarioId?: string;
        provider?: string;
        questionnaireIds?: string[];
        interactionMode?: string;
      } = {},
    ) =>
      req<{ id: string; status: string }>(SCORING_BASE, {
        method: "POST",
        body: JSON.stringify({
          transcript_object_id: transcriptObjectId,
          ...(opts.scenarioId ? { scenario_id: opts.scenarioId } : {}),
          ...(opts.provider ? { provider: opts.provider } : {}),
          ...(opts.interactionMode ? { interaction_mode: opts.interactionMode } : {}),
          ...(opts.questionnaireIds?.length
            ? {
                questionnaire_ids: opts.questionnaireIds,
                questionnaire_object_id: opts.questionnaireIds[0],
              }
            : {}),
        }),
        op: "start-scoring-job",
      }),
    get: (transcriptObjectId: string) =>
      req<ScoringJobResponse>(`${SCORING_BASE}/${transcriptObjectId}`, {
        op: "get-scoring-job",
      }),
    getPublic: (transcriptObjectId: string, publicLinkToken: string) =>
      req<ScoringJobResponse>(
        `${SCORING_BASE}/${transcriptObjectId}${qs({ public_link_token: publicLinkToken })}`,
        { skipUserAuth: true, op: "get-scoring-job" },
      ),
  },

  user: {
    groups: () => reqList<UserGroup>(`/api/v1.0/user/groups`, { op: "list-user-groups" }),
  },
};

/**
 * Fire-and-forget POST that survives page unload (uses `fetch` with
 * `keepalive`). Records an authenticated live-avatar session when the
 * host iframe/tab is closed and we can't await a response.
 */
export function powtoonBESessionBeacon(input: {
  scenarioId: string;
  content: Record<string, unknown>;
}): void {
  try {
    const body = JSON.stringify({
      object_type: "live_avatar_session",
      name: "Live Avatar Session",
      content: input.content,
    });
    fetch(
      appendOp(
        `${INTEGRATION_BASE}${BASE}/${input.scenarioId}/objects`,
        "track-session-beacon",
      ),
      {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...authHeaders(),
      },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}